import React from 'react';
import { render } from 'react-dom';
import branch from 'branch-sdk';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { reCaptchaSiteKey, fbqPixelId } from '#config/definitions';
import * as Sentry from '@sentry/react';
import { TokenizedApiProvider } from '@tokenized/sdk-react-private';
import { IntlProvider } from 'react-intl';
import { toast, Slide } from 'react-toastify';
import './app.global.scss';
// TODO: Remove the dependency on these Bulma global styles that are
// currently vital to style inputs correctly:
import '../components/Elements/Form/Form.scss';
import messagesEn from '../lang-compiled/en.json';
import {
  initGoogleAnalytics,
  initClarity,
  captureFacebookClickId,
} from '#modules/utils/analytics';
import { checkAppVersion } from '#modules/utils/appVersionCheck';
import { LOGOUT } from '#modules/state/LogOutAction';
import { TooltipProvider } from '#modules/context/TooltipContext';
import { BankingProvider } from '#modules/context/BankingContext';
import { ModalProvider } from '#modules/context/ModalContext';
import { LockboxProvider } from '#modules/context/LockboxContext';
import { EntityProvider } from '#modules/context/EntityContext';
import { ErrorHandlingProvider } from '#modules/context/ErrorHandlingContext';
import { InboxProvider } from '#modules/context/InboxContext';
import configureStore from '#app/store/configureStore';
import App from '#app/containers/App';
import SessionRestoreSpinner from '#app/containers/SessionRestoreSpinner';
import ThemeLoader from '#app/containers/ThemeLoader';
import {
  loginConfiguration,
  sentryDSN,
  sentryDist,
  version,
  branchApiKey,
  liquidNobleApiUrl,
} from '#config/definitions';
import { isDebugBuild } from '#config/shared';
import {
  getTokenizedBackend,
  getApiUrl,
  setRendererParams,
} from '#config/renderer';
import { sourceName } from '#app-config';
import styles from './styles/toast.scss';

Error.stackTraceLimit = 1000;

if (sentryDSN) {
  Sentry.init({
    dsn: sentryDSN,
    release: version,
    dist: sentryDist,
    debug: isDebugBuild,
    integrations: [
      //Sentry.replayIntegration()
    ],
    beforeSend(event) {
      if (event.request && event.request.url) {
        event.request.url = event.request.url.replace(
          /code=[^&]+/,
          'code=REDACTED',
        );
      }

      // Add redaction for transaction data
      if (event.transaction) {
        event.transaction = event.transaction.replace(
          /code=[^&]+/,
          'code=REDACTED',
        );
      }

      // Redact URLs in spans if present
      if (event.spans) {
        event.spans.forEach((span) => {
          if (span.description) {
            span.description = span.description.replace(
              /code=[^&]+/,
              'code=REDACTED',
            );
          }
          if (span.data && span.data.url) {
            span.data.url = span.data.url.replace(
              /code=[^&]+/,
              'code=REDACTED',
            );
          }
        });
      }

      return event;
    },
    beforeBreadcrumb(breadcrumb, hint) {
      if (
        breadcrumb.category === 'http' &&
        breadcrumb.data &&
        breadcrumb.data.url
      ) {
        breadcrumb.data.url = breadcrumb.data.url.replace(
          /code=[^&]+/,
          'code=REDACTED',
        );
      }
      return breadcrumb;
    },
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/api\.liquidnoble\.com/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (branchApiKey) {
  branch.init(branchApiKey, {}, function (err, data) {
    if (data && Object.keys(data.data_parsed).length) {
      const existingBranchData = window.localStorage.getItem('__BRANCH__');
      if (existingBranchData) {
        const existingBranchDataParsed = JSON.parse(existingBranchData);
        // Do not overwrite existing referral code
        if (existingBranchDataParsed['referral_code']) {
          return;
        }
      }
      window.localStorage.setItem(
        '__BRANCH__',
        JSON.stringify(data.data_parsed),
      );
    }
  });
}

// The Electron main process passes parameters to this code starting up
// inside a window it just created via the URL query string
const searchParamsOnStartup = new URLSearchParams(window?.location?.search);
try {
  const runtimeParams = searchParamsOnStartup.get('runtimeParams');
  if (runtimeParams) {
    const parsedRuntimeParams = JSON.parse(runtimeParams);
    console.log(`runtimeParams = ${JSON.stringify(parsedRuntimeParams)}`);
    setRendererParams(parsedRuntimeParams);
  }
} catch (error) {
  console.error(error);
}

/**
 * Check for critical configuration values that should always be present.
 * If missing, the page was likely restored from bfcache without config.
 * Force a hard reload to fetch fresh config from the server.
 */
function checkCriticalConfig() {
  // Skip check if we already tried reloading (prevent infinite loops)
  const hasReloaded = sessionStorage.getItem('__config_reload_attempted');
  if (hasReloaded) {
    console.warn(
      'checkCriticalConfig: Config reload was already attempted, proceeding anyway',
    );
    sessionStorage.removeItem('__config_reload_attempted');
    return false;
  }

  const missingConfig = [];

  if (!reCaptchaSiteKey) {
    missingConfig.push('reCaptchaSiteKey');
  }

  if (!liquidNobleApiUrl) {
    missingConfig.push('liquidNobleApiUrl');
  }

  if (missingConfig.length > 0) {
    console.error(
      `checkCriticalConfig: Critical config missing: ${missingConfig.join(', ')}. ` +
        'Page may have been restored from cache. Forcing reload...',
    );

    // Mark that we attempted a reload
    sessionStorage.setItem('__config_reload_attempted', 'true');

    // Force reload from server (cache-busting)
    window.location.reload();
    return true;
  }

  console.log(
    'checkCriticalConfig: Critical config present, proceeding normally',
  );
  return false;
}

// Check on initial load
const isReloading = checkCriticalConfig();

// Detect a stale build (version.json mismatch) and force a fresh document load
checkAppVersion();

// A page restored from bfcache is still running the bundle that was loaded
// before the restore. On an installed PWA (Android), closing and reopening
// the app can restore the page from bfcache without any new index.html
// request, so a freshly deployed build is never fetched. Force a fresh
// document load in standalone mode to pick up the current build.
function isStandaloneMode() {
  return (
    (window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

// Check when page is restored from bfcache (back-forward cache)
// This fires when a tab is restored after being discarded/reopened
if (!isReloading && typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    // persisted is true when page was restored from bfcache
    if (event.persisted) {
      console.log(
        'checkCriticalConfig: Page restored from bfcache, checking config...',
      );
      if (checkCriticalConfig()) {
        return;
      }
      if (isStandaloneMode()) {
        console.log(
          'Standalone PWA restored from bfcache, reloading to fetch current build...',
        );
        window.location.reload();
      }
    }
  });
}

const store = configureStore();

const Router = BrowserRouter;

toast.configure({
  position: toast.POSITION.BOTTOM_CENTER,
  autoClose: 10000,
  className: styles.toast,
  bodyClassName: styles.toastBody,
  hideProgressBar: true,
  closeButton: false,
  draggable: false,
  transition: Slide,
});

let authResponse;

if (location.pathname == '/auth-code' && location.hash) {
  authResponse = location.hash.slice(1);
  location.hash = '';
}

let locale =
  window.navigator.languages?.[0] || window.navigator.language || 'en';

// Locale identifiers for testing:
//
// European locales with comma and period separators swapped from English,
// and with currency symbols on the right:
// locale = 'fr-FR';
// locale = 'de-DE';
//
// Locales with right-to-left-written languages and Eastern Arabic numerals:
// locale = 'ar-EG-u-nu-arab';
// locale = 'fa-IR';
//
// Other examples of alternative numeral systems:
// locale = 'fr-Latn-FR-u-nu-mong';
// locale = 'zh-Hans-CN-u-nu-hanidec';
// locale = 'th-TH-u-nu-thai';

const tokenizedBackend = getTokenizedBackend();

console.log({ loginConfiguration });

export const config = {
  authResponse,
  loginConfiguration,
  restApiBaseUrl: undefined,
  sseBaseUrl: undefined,
  tokenizedBackend: tokenizedBackend,
  userAgentString: `desktop-${version}`,
  applicationIdentifier: 'customer-app',
  sourceSystemInfo: sourceName,
  accountCreationSupported: true,
  errorTransform: sentryDSN
    ? (error) => {
        // Pass API errors to Sentry
        Sentry.captureException(error);
      }
    : undefined,
  locale,
  onLogOut: () => {
    // Clear the Redux store
    store.dispatch({ type: LOGOUT });
    // Evict everything from memory
    location.reload();
  },
};

function initFacebookAnalytics() {
  if (!window?.fbq) {
    console.log('FBQ not loaded');
    return;
  }
  if (!fbqPixelId) {
    console.log('FBQ ID not set');
    return;
  }
  window?.fbq('init', fbqPixelId);
  console.log('FBQ initialized');
}

async function initAllAnalytics() {
  try {
    // Capture Facebook click ID from URL early (before navigation changes URL)
    captureFacebookClickId();

    // Initialize Facebook Pixel
    initFacebookAnalytics();

    // Initialize GA + GTM
    await initGoogleAnalytics();
    initClarity();
    console.log('Analytics initialized successfully');
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
}

initAllAnalytics();

render(
  <GoogleReCaptchaProvider reCaptchaKey={reCaptchaSiteKey}>
    <TokenizedApiProvider config={config}>
      <IntlProvider locale={locale} messages={messagesEn}>
        <ThemeLoader>
          <SessionRestoreSpinner>
            <ReduxProvider store={store}>
              <ErrorHandlingProvider>
                <ModalProvider>
                  <EntityProvider>
                    <TooltipProvider>
                      <BankingProvider>
                        <LockboxProvider>
                          <InboxProvider>
                            <Router>
                              <App />
                            </Router>
                          </InboxProvider>
                        </LockboxProvider>
                      </BankingProvider>
                    </TooltipProvider>
                  </EntityProvider>
                </ModalProvider>
              </ErrorHandlingProvider>
            </ReduxProvider>
          </SessionRestoreSpinner>
        </ThemeLoader>
      </IntlProvider>
    </TokenizedApiProvider>
  </GoogleReCaptchaProvider>,
  document.getElementById('root'),
);
